import React, { useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import CockpitLayout from './CockpitLayout';
import LoginButton from './LoginButton';
import { apiClient } from '../services/apiClient';
import { loginRequest } from '../authConfig';

const AuthenticatedApp: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  
  // Ensure active account is set when user is authenticated (Microsoft best practice)
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
      console.log('✅ Active account set in AuthenticatedApp:', accounts[0].username);
    }
    
    console.log('🔐 Authentication state:', {
      isAuthenticated,
      accountCount: accounts.length,
      activeAccount: instance.getActiveAccount()?.username || 'none',
      allAccounts: accounts.map(acc => acc.username)
    });
  }, [isAuthenticated, accounts, instance]);

  // Acquire and set access token for API calls
  useEffect(() => {
    const acquireAndSetToken = async (forceRefresh = false) => {
      if (isAuthenticated && accounts.length > 0) {
        try {
          console.log(`🔐 Acquiring access token${forceRefresh ? ' (forced refresh)' : ''}...`);
          
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: forceRefresh // Force refresh to ensure fresh token
          });
          
          if (!response.accessToken) {
            throw new Error('No access token in response');
          }
          
          // Check token expiration
          const expiresOn = response.expiresOn;
          if (expiresOn) {
            const timeUntilExpiry = expiresOn.getTime() - Date.now();
            console.log(`✅ Token acquired, expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes`);
          }
          
          apiClient.setAuthToken(response.accessToken);
          console.log('🔐 Fresh token set on apiClient - backend will receive valid Authorization header');
        } catch (error: any) {
          console.error('❌ Failed to acquire access token silently:', error);
          
          // Try interactive login as fallback using redirect flow
          try {
            console.log('🔄 Attempting interactive token acquisition via redirect...');
            await instance.acquireTokenRedirect(loginRequest);
            // Note: Code after acquireTokenRedirect won't execute as the page will redirect
          } catch (redirectError) {
            console.error('❌ Interactive token acquisition also failed:', redirectError);
            // Clear any stale tokens
            apiClient.setAuthToken(null);
          }
        }
      }
    };

    // Force refresh on initial mount to ensure fresh token after login
    acquireAndSetToken(true);
    
    // Refresh token more frequently (every 10 minutes) to stay ahead of expiration
    const interval = setInterval(() => acquireAndSetToken(true), 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, accounts, instance]);
  
  if (!isAuthenticated || accounts.length === 0) {
    console.log('🚪 User not authenticated - showing login screen');
    return <LoginButton />;
  }

  console.log('✅ User authenticated - showing dashboard');
  return <CockpitLayout />;
};

export default AuthenticatedApp;
